import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import Select from '../../ui/selects/Select';
import { BanknoteArrowUp, Car, Pen, Trash2, X } from 'lucide-react';
import { useToast } from '../../ui/toast/ToastContext';
import SecondaryButton from '../../ui/buttons/SecondaryButton';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import AddBaseModal from './AddBaseModal';
import DashboardTab from '../../../pages/planner/client/dashboard/DashboardTab';
import AssetStatusBadge from '../../ui/badges/AssetStatusBadge';
import EditBaseModal from './EditBaseModal';
import AddAssetModal from './AddAssetModal';
import Pagination from '../../ui/pagination/Pagination';

const ManageBasesModal = ({ onClose, onSuccess }) => {
  const { addToast } = useToast();
  const [showAddBaseModal, setShowAddBaseModal] = useState(false);
  const [showEditBaseModal, setShowEditBaseModal] = useState(false);
  const [showAddAssetModal, setShowAddAssetModal] = useState(false);
  const [editBaseId, setEditBaseId] = useState(null);
  const [activeTab, setActiveTab] = useState('bases');
  const [data, setData] = useState({ bases: [], manualAssets: [] });
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
  const [loading, setLoading] = useState(false);

  const tabs = [
    { key: 'bases', label: 'BASES IMPORTADAS' },
    { key: 'manual_assets', label: 'ATIVOS ADICIONADOS MANUALMENTE' },
  ];

  const buildQuery = (params) =>
    Object.entries(params)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');

  const fetchData = async (page = 1, tabKey) => {
    try {
      setLoading(true);
      const query = buildQuery({ page });
      const res = await fetchWithCsrf(`/app/analyst/assets/${tabKey}?${query}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const json = await res.json();
      const parsed = camelizeKeys(json);

      console.log('Dados carregados:', parsed);

      setData((prev) => ({ ...prev, ...parsed.data }));
      setPagination(parsed.pagination);
    } catch (err) {
      console.error('Erro ao carregar dados', err);
      addToast({ type: 'error', message: 'Erro ao carregar dados' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, activeTab);
  }, [activeTab]);

  // Handle tab change
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  const handlePageChange = (newPage) => {
    fetchData(newPage, activeTab);
  };

  // Asset Type
  const assetTypes = [
    { key: 'investment_funds', label: 'Fundos de Investimento' },
    { key: 'pension_funds', label: 'Fundos de Previdência' },
    { key: 'equities', label: 'Renda Variável' },
  ];

  return (
    <Modal onClose={onClose}>
      <div className="p-10 flex flex-col gap-4">
        {/* Header */}
        <div className=" flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <BanknoteArrowUp size={24} className="text-tealMedium" strokeWidth={1.25} />
            Gerenciar Bases
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>
        <div className="flex gap-4 justify-between items-center">
          {tabs.map((tab) => (
            <DashboardTab
              key={tab.key}
              label={tab.label}
              active={activeTab === tab.key}
              completed={tab.completed}
              onClick={() => handleTabChange(tab.key)}
              inactiveColor={'bg-blueLight text-blueDarker border-blueLight hover:bg-blueSoft'}
            />
          ))}
        </div>
        {activeTab === 'bases' && (
          <div>
            <div className="space-y-0 overflow-y-auto" style={{ height: '320px' }}>
              {data.bases.map((b, idx) => {
                const isBlueRow = idx % 2 !== 1;
                return (
                  <div key={b.id} className={`${isBlueRow ? 'bg-blueLight' : 'bg-transparent'} rounded-xl`}>
                    <div
                      className="grid items-center gap-4 px-3 py-3"
                      style={{
                        gridTemplateColumns: 'minmax(0, 1fr) auto',
                      }}
                    >
                      <div
                        className="min-w-0 flex items-center gap-2 font-semibold text-blueDarker"
                        title={b.description}
                      >
                        <span
                          className="flex-none block rounded"
                          style={{ backgroundColor: b.colorHex, width: '12px', height: '12px' }}
                        />
                        <span className="min-w-0 truncate">{b.description}</span>
                        <span className="min-w-0 truncate font-normal italic text-sm text-blueDarker/70">
                          {assetTypes.find((type) => type.key === b.assetType)?.label}
                        </span>
                      </div>

                      <div className="flex gap-3 items-center justify-end">
                        <button
                          className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
                          onClick={() => setShowEditBaseModal(true) & setEditBaseId(b.id)}
                        >
                          EDITAR
                        </button>
                        <button
                          className="bg-redSoft hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-3 pb-2 text-xs border-r-2 border-b-2 border-redDeep"
                          onClick={() => setShowEditAssetModal(true) & setEditAssetId(c.id)}
                        >
                          <Trash2 size={16} className="inline-block" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {data.bases.length === 0 ? (
                <div className="bg-white rounded-custom20 p-6 text-center text-grayDark">Nenhuma base encontrada.</div>
              ) : null}
            </div>
            <div className="flex justify-center items-center mt-4">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>

            <div className="flex justify-end items-center mt-4">
              <div className="flex justify-between gap-2">
                <SecondaryButton onClick={onClose}>CANCELAR</SecondaryButton>
                <Button onClick={() => setShowAddBaseModal(true)}>ADICIONAR BASE</Button>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'manual_assets' && (
          <div>
            <div className="space-y-0 overflow-y-auto" style={{ height: '320px' }}>
              {data.manualAssets.map((m, idx) => {
                const isBlueRow = idx % 2 !== 1;
                return (
                  <div key={m.id} className={`${isBlueRow ? 'bg-blueLight' : 'bg-transparent'} rounded-xl`}>
                    <div
                      className="grid items-center gap-4 px-3 py-3"
                      style={{
                        gridTemplateColumns: 'minmax(0, 1fr) 180px auto',
                      }}
                    >
                      <span className="min-w-0 truncate font-semibold">{m.displayName}</span>

                      <span className="min-w-0 truncate">{m.tType}</span>

                      <div className="flex gap-3 items-center justify-end">
                        <button
                          className="bg-tealMedium hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-3 pb-2 text-xs border-r-2 border-b-2 border-tealBase"
                          onClick={() => setShowEditBaseModal(true) & setEditBaseId(m.id)}
                        >
                          EDITAR
                        </button>
                        <button
                          className="bg-redSoft hover:opacity-90 transition-opacity text-white font-semibold rounded-xl px-4 pt-3 pb-2 text-xs border-r-2 border-b-2 border-redDeep"
                          onClick={() => setShowEditAssetModal(true) & setEditAssetId(m.id)}
                        >
                          <Trash2 size={16} className="inline-block" strokeWidth={2} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {data.manualAssets.length === 0 ? (
                <div className="bg-white rounded-custom20 p-6 text-center text-grayDark">Nenhum ativo encontrado.</div>
              ) : null}
            </div>
            <div className="flex justify-center items-center mt-4">
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </div>
            <div className="flex justify-end items-center mt-4">
              <div className="flex justify-between gap-2">
                <SecondaryButton onClick={onClose}>CANCELAR</SecondaryButton>
                <Button onClick={() => setShowAddAssetModal(true)}>ADICIONAR ATIVO</Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {showAddBaseModal && (
        <AddBaseModal
          onClose={() => setShowAddBaseModal(false)}
          onSuccess={() => {
            setShowAddBaseModal(false);
          }}
        />
      )}
      {showEditBaseModal && (
        <EditBaseModal
          baseId={editBaseId}
          onClose={() => setShowEditBaseModal(false)}
          onSuccess={() => {
            setShowEditBaseModal(false);
            fetchData(1, 'bases');
          }}
        />
      )}
      {showAddAssetModal && (
        <AddAssetModal
          onClose={() => setShowAddAssetModal(false)}
          onSuccess={() => {
            setShowAddAssetModal(false);
            fetchData(1, 'manual_assets');
          }}
        />
      )}
    </Modal>
  );
};

export default ManageBasesModal;
